import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Chip, Stack, Switch, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, Table, TableHead, TableRow, TableCell, TableBody, Grid,
  FormControlLabel, Divider, Tooltip, Checkbox, LinearProgress,
} from '@mui/material';
import { Add, Edit, Phone, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';

// Destination Filters: per-customer + destination-prefix traffic rules checked by the SBC before a call
// reaches the carrier. Customers/destinations without a filter are not affected. All values live in the
// database and take effect on the next call — no restart.

const MODES = [{ v: 'block', l: 'Block' }, { v: 'alert', l: 'Alert only' }, { v: 'off', l: 'Off' }];
const modeColor = (m: string): any => (m === 'block' ? 'error' : m === 'alert' ? 'warning' : 'default');
const int = (v: any) => Number(v || 0).toLocaleString('en-US');
const EMPTY = {
  tenant_id: '', dst_prefix: '', label: '', enabled: true, max_cps: 2.5, cps_mode: 'block', number_regex: '', format_mode: 'block',
  max_per_dst_day: 1, repeat_cooldown_min: '', repeat_mode: 'block', dead_codes: '404', dead_days: 30, dead_mode: 'block', hours_start: '09:00',
  hours_end: '20:00', hours_tz: 'UTC', hours_mode: 'block', daily_alert_cap: 40000, no_retry_busy: true, pai_pool: '',
  pool_daily_cap_per_number: 100, notes: '',
};

export const DestinationFiltersPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [pool, setPool] = useState<any>(null);
  const [poolFor, setPoolFor] = useState<any>(null);
  const [addBlock, setAddBlock] = useState('');
  const [addCount, setAddCount] = useState('50');
  const [sel, setSel] = useState<string[]>([]);
  const [events, setEvents] = useState<any[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/dest-policy'); setRows(data || []); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed to load filters', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/tenants', { params: { limit: 500 } }).then((r) => setTenants(r.data?.data ?? r.data ?? [])).catch(() => undefined); }, []);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const body: any = { ...edit };
      ['id', 'tenant_name', 'today_attempts', 'today_allowed', 'today_blocked', 'today_alerted', 'pool_size', 'last24h', 'updated_at', 'updated_by']
        .forEach((k) => delete body[k]);
      if (edit.id) { delete body.tenant_id; delete body.dst_prefix; await api.put(`/dest-policy/${edit.id}`, body); }
      else await api.post('/dest-policy', body);
      setEdit(null); enqueueSnackbar('Filter saved — applies to the next call', { variant: 'success' }); load();
    } catch (e: any) { setErr(e?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const toggle = async (r: any, enabled: boolean) => {
    try { await api.put(`/dest-policy/${r.id}`, { enabled }); load(); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed', { variant: 'error' }); }
  };

  const del = async (r: any) => {
    if (!window.confirm(`Delete the filter ${r.label || r.tenant_name + ' → +' + r.dst_prefix}? Calls to +${r.dst_prefix} will no longer be filtered.`)) return;
    await api.delete(`/dest-policy/${r.id}`); load();
  };

  const openPool = async (r: any) => {
    setPoolFor(r); setSel([]);
    try { const { data } = await api.get(`/dest-policy/${r.id}/pool`); setPool(data); setAddBlock(data.available?.[0]?.block || ''); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed to load pool', { variant: 'error' }); setPoolFor(null); }
  };
  const poolAdd = async () => {
    try { const { data } = await api.post(`/dest-policy/${poolFor.id}/pool/add`, { block: addBlock, count: Number(addCount) }); enqueueSnackbar(`${data.added} numbers added to the pool`, { variant: 'success' }); openPool(poolFor); load(); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed', { variant: 'error' }); }
  };
  const setCallbacks = async (mode: string) => {
    try { const { data } = await api.post(`/dest-policy/${poolFor.id}/pool/callbacks`, { mode }); enqueueSnackbar(`${data.updated} numbers: callbacks now ${mode === 'announcement' ? 'play the busy announcement' : 'go to the customer'}`, { variant: 'success' }); openPool(poolFor); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed', { variant: 'error' }); }
  };
  const poolRelease = async () => {
    if (!window.confirm(`Release ${sel.length} numbers back to the free inventory?`)) return;
    try { const { data } = await api.post(`/dest-policy/${poolFor.id}/pool/release`, { numbers: sel }); enqueueSnackbar(`${data.released} numbers released`, { variant: 'success' }); openPool(poolFor); load(); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || 'Failed', { variant: 'error' }); }
  };
  const openEvents = async (r: any) => {
    try { const { data } = await api.get(`/dest-policy/${r.id}/events`, { params: { limit: 300 } }); setEvents(data); setPoolFor(r); }
    catch { enqueueSnackbar('Failed to load events', { variant: 'error' }); }
  };

  const F = (k: string, label: string, props: any = {}) => (
    <TextField fullWidth size="small" label={label} value={edit?.[k] ?? ''} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} {...props} />
  );
  const M = (k: string, label: string) => (
    <TextField select fullWidth size="small" label={label} value={edit?.[k] || 'off'} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}>
      {MODES.map((m) => <MenuItem key={m.v} value={m.v}>{m.l}</MenuItem>)}
    </TextField>
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={700}>Destination Filters</Typography>
          <Typography variant="body2" color="text.secondary">
            Per-customer rules for a destination prefix, checked before a call reaches the carrier. Customers and destinations without a filter are not affected. Changes apply to the next call.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setErr(''); setEdit({ ...EMPTY }); }}>Add filter</Button>
      </Stack>
      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}
      {!rows.length && !loading ? <Alert severity="info">No destination filters yet.</Alert> : null}

      <Stack spacing={2}>
        {rows.map((r) => (
          <Card key={r.id} variant="outlined">
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={700}>{r.label || `${r.tenant_name} → +${r.dst_prefix}`}</Typography>
                  <Typography variant="body2" color="text.secondary">Customer {r.tenant_name} · destinations starting +{r.dst_prefix}</Typography>
                </Box>
                <FormControlLabel control={<Switch checked={!!r.enabled} onChange={(e) => toggle(r, e.target.checked)} />} label={r.enabled ? 'Active' : 'Disabled'} />
                <Button size="small" startIcon={<Edit />} onClick={() => { setErr(''); setEdit({ ...r, hours_start: r.hours_start?.slice(0, 5), hours_end: r.hours_end?.slice(0, 5) }); }}>Edit</Button>
                {r.pai_pool ? <Button size="small" startIcon={<Phone />} onClick={() => openPool(r)}>Caller-ID pool ({r.pool_size})</Button> : null}
                <Button size="small" onClick={() => openEvents(r)}>Events</Button>
                <Tooltip title="Delete filter"><Button size="small" color="error" onClick={() => del(r)}><Delete fontSize="small" /></Button></Tooltip>
              </Stack>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" color={modeColor(r.cps_mode)} label={`CPS ${r.max_cps ?? '—'} · ${r.cps_mode}`} />
                <Chip size="small" color={modeColor(r.repeat_mode)} label={`Repeat: ${[r.repeat_cooldown_min ? `${r.repeat_cooldown_min} min cooldown` : '', r.max_per_dst_day ? `max ${r.max_per_dst_day}/number/day` : ''].filter(Boolean).join(', ') || 'no limit'} · ${r.repeat_mode}`} />
                <Chip size="small" color={modeColor(r.dead_mode)} label={`Dead numbers (${r.dead_codes || '—'}, ${r.dead_days}d) · ${r.dead_mode}`} />
                <Chip size="small" color={modeColor(r.format_mode)} label={`Number format · ${r.format_mode}`} />
                <Chip size="small" color={modeColor(r.hours_mode)} label={`Hours ${r.hours_start?.slice(0, 5) || '—'}–${r.hours_end?.slice(0, 5) || '—'} ${r.hours_tz || ''} · ${r.hours_mode}`} />
                <Chip size="small" color="warning" variant="outlined" label={`Daily alert at ${int(r.daily_alert_cap)} (alert only)`} />
                <Chip size="small" variant="outlined" label={r.no_retry_busy ? 'No retry on busy/decline' : 'Retries on busy/decline'} />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Today (UTC): <b>{int(r.today_attempts)}</b> attempts · <b>{int(r.today_allowed)}</b> passed · <b style={{ color: '#c62828' }}>{int(r.today_blocked)}</b> blocked · <b>{int(r.today_alerted)}</b> alerted
                {(r.last24h || []).length ? ` · last 24 h: ${(r.last24h || []).map((x: any) => `${x.rule} ${x.action} ${int(x.n)}`).join(', ')}` : ''}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Add / edit */}
      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="md" fullWidth>
        <DialogTitle>{edit?.id ? 'Edit filter' : 'Add filter'}</DialogTitle>
        <DialogContent>
          {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Customer" value={edit?.tenant_id || ''} disabled={!!edit?.id}
                onChange={(e) => setEdit({ ...edit, tenant_id: e.target.value })}>
                {tenants.map((t: any) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>{F('dst_prefix', 'Destination prefix (e.g. 852)', { disabled: !!edit?.id })}</Grid>
            <Grid item xs={6} sm={3}>{F('label', 'Label')}</Grid>

            <Grid item xs={12}><Typography variant="subtitle2">Calls per second</Typography></Grid>
            <Grid item xs={6} sm={3}>{F('max_cps', 'Max CPS', { type: 'number', inputProps: { step: 0.5, min: 0 } })}</Grid>
            <Grid item xs={6} sm={3}>{M('cps_mode', 'Action')}</Grid>

            <Grid item xs={12}><Typography variant="subtitle2">Repeat dialling and dead numbers</Typography></Grid>
            <Grid item xs={6} sm={3}>{F('repeat_cooldown_min', 'Repeat cooldown (minutes)', { type: 'number', helperText: 'Same number blocked for N min after a call goes through' })}</Grid>
            <Grid item xs={6} sm={3}>{F('max_per_dst_day', 'Max attempts per number per day', { type: 'number', helperText: 'Empty = no daily limit' })}</Grid>
            <Grid item xs={6} sm={3}>{M('repeat_mode', 'Action')}</Grid>
            <Grid item xs={6} sm={2}>{F('dead_codes', 'Dead codes')}</Grid>
            <Grid item xs={6} sm={2}>{F('dead_days', 'Days', { type: 'number' })}</Grid>
            <Grid item xs={6} sm={2}>{M('dead_mode', 'Action')}</Grid>

            <Grid item xs={12}><Typography variant="subtitle2">Number format and calling hours</Typography></Grid>
            <Grid item xs={12} sm={6}>{F('number_regex', 'Valid number pattern (digits, no +)', { helperText: 'e.g. ^852[235679][0-9]{7}$' })}</Grid>
            <Grid item xs={6} sm={3}>{M('format_mode', 'Action')}</Grid>
            <Grid item xs={6} sm={3} />
            <Grid item xs={4} sm={2}>{F('hours_start', 'From', { type: 'time', InputLabelProps: { shrink: true } })}</Grid>
            <Grid item xs={4} sm={2}>{F('hours_end', 'To', { type: 'time', InputLabelProps: { shrink: true } })}</Grid>
            <Grid item xs={4} sm={3}>{F('hours_tz', 'Time zone', { helperText: 'e.g. Asia/Hong_Kong' })}</Grid>
            <Grid item xs={6} sm={3}>{M('hours_mode', 'Action')}</Grid>

            <Grid item xs={12}><Typography variant="subtitle2">Volume alert, retries and caller ID</Typography></Grid>
            <Grid item xs={6} sm={3}>{F('daily_alert_cap', 'Daily alert threshold (alert only)', { type: 'number' })}</Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel control={<Switch checked={!!edit?.no_retry_busy} onChange={(e) => setEdit({ ...edit, no_retry_busy: e.target.checked })} />} label="No retry on 486/487/6xx" />
            </Grid>
            <Grid item xs={6} sm={3}>{F('pai_pool', 'Caller-ID pool name', { helperText: 'blank = customer’s normal numbers' })}</Grid>
            <Grid item xs={6} sm={3}>{F('pool_daily_cap_per_number', 'Calls per pool number per day', { type: 'number' })}</Grid>
            <Grid item xs={12}>{F('notes', 'Notes', { multiline: true, minRows: 2 })}</Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Pool */}
      <Dialog open={!!pool && !!poolFor && !events} onClose={() => { setPool(null); setPoolFor(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Caller-ID pool “{pool?.pool}” — {pool?.numbers?.length || 0} numbers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These numbers are presented only on calls matching filters that use this pool. They stay assigned to the customer and ring back to the customer’s trunk. The customer’s other numbers are not used for these calls.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <TextField select size="small" label="When someone calls these numbers back" value={pool?.callbacks === 'mixed' ? '' : (pool?.callbacks || '')}
              onChange={(e) => setCallbacks(e.target.value)} sx={{ minWidth: 340 }}
              helperText={pool?.callbacks === 'mixed' ? 'Mixed — pick one to apply to all pool numbers' : 'Applies to every number in this pool, immediately'}>
              <MenuItem value="announcement">Play “all agents busy” announcement (max 20 at once)</MenuItem>
              <MenuItem value="customer">Deliver to the customer’s trunk</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <TextField select size="small" label="Add from free block" value={addBlock} onChange={(e) => setAddBlock(e.target.value)} sx={{ minWidth: 260 }}>
              {(pool?.available || []).map((b: any) => <MenuItem key={b.block} value={b.block}>+{b.block}… {b.area || ''} ({b.free} free)</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="How many" value={addCount} onChange={(e) => setAddCount(e.target.value)} sx={{ width: 120 }} />
            <Button variant="outlined" disabled={!addBlock} onClick={poolAdd}>Add to pool</Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button color="error" disabled={!sel.length} onClick={poolRelease}>Release selected ({sel.length})</Button>
          </Stack>
          <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell padding="checkbox" /><TableCell>Number</TableCell><TableCell>Area</TableCell><TableCell align="right">Calls today</TableCell><TableCell>Last used</TableCell></TableRow></TableHead>
              <TableBody>
                {(pool?.numbers || []).map((n: any) => (
                  <TableRow key={n.number} hover>
                    <TableCell padding="checkbox"><Checkbox size="small" checked={sel.includes(n.number)} onChange={(e) => setSel(e.target.checked ? [...sel, n.number] : sel.filter((x) => x !== n.number))} /></TableCell>
                    <TableCell>{n.number}</TableCell><TableCell>{n.area_name || '—'}</TableCell>
                    <TableCell align="right">{int(n.today_calls)}</TableCell>
                    <TableCell>{n.pai_last_used_at ? new Date(n.pai_last_used_at).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC' : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => { setPool(null); setPoolFor(null); }}>Close</Button></DialogActions>
      </Dialog>

      {/* Events */}
      <Dialog open={!!events} onClose={() => { setEvents(null); setPoolFor(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Recent filter events — {poolFor?.label || poolFor?.tenant_name}</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead><TableRow><TableCell>Time (UTC)</TableCell><TableCell>Rule</TableCell><TableCell>Action</TableCell><TableCell>Number</TableCell></TableRow></TableHead>
            <TableBody>
              {(events || []).map((e: any, i: number) => (
                <TableRow key={i}>
                  <TableCell>{new Date(e.ts).toLocaleString('en-GB', { timeZone: 'UTC' })}</TableCell>
                  <TableCell>{e.rule}</TableCell>
                  <TableCell><Chip size="small" color={e.action === 'blocked' ? 'error' : 'warning'} label={e.action} /></TableCell>
                  <TableCell>{e.dst}</TableCell>
                </TableRow>
              ))}
              {!events?.length ? <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No events</TableCell></TableRow> : null}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => { setEvents(null); setPoolFor(null); }}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default DestinationFiltersPage;
